import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { AppContext } from '@rabbit/app-context';

/**
 * Prompt inviting the signed-in or signing-in business to move to the new
 * platform.
 *
 * Renders nothing unless the active tenant config turns the migration on and
 * supplies a destination, so no tenant slug appears here and a tenant that is
 * not migrating never sees it.
 *
 * The destination page does the work: the business enters the email address we
 * already hold for them and is sent a link to set a password on the new
 * platform. Nothing here signs them out or changes anything on this platform -
 * a dealer can keep working here until they choose to move.
 */
export function PlatformMigrationNotice({
  variant = 'card',
}: {
  /** `card` sits inside the login panel; `banner` spans the top of the app. */
  variant?: 'card' | 'banner';
}) {
  const { t } = useTranslation();
  const { config } = useContext(AppContext);
  const migration = config?.PLATFORM_MIGRATION;

  if (!migration?.ENABLED || !migration?.URL) return null;

  const isBanner = variant === 'banner';

  return (
    <div
      className={classNames(
        'border-primary-200 bg-primary-50 flex flex-col gap-3',
        isBanner
          ? 'border-b px-4 py-3 md:flex-row md:items-center md:justify-between md:px-8'
          : 'rounded-lg border p-4'
      )}
    >
      <div className="flex flex-col gap-1">
        <p className="font-nunito text-base font-semibold text-gray-800">
          {t(
            'message.platformMigrationTitle',
            'We are moving to a new platform'
          )}
        </p>
        <p className="font-nunito text-sm text-gray-600">
          {t(
            'message.platformMigrationBody',
            'You can move your account across whenever you are ready.'
          )}
        </p>
      </div>
      {/* A styled anchor, not <a><Button/></a>: nesting a <button> inside an
          <a> is invalid HTML (interactive content cannot nest) and confuses
          screen readers. This navigates, so a real link is also the right
          semantic. Button's dynamicStyles are module-private, so the primary/sm
          look is spelled out here rather than imported. */}
      <a
        href={migration.URL}
        target="_blank"
        rel="noopener noreferrer"
        className={classNames(
          'font-nunito bg-primary-900 hover:bg-primary-700 flex h-[34px] flex-shrink-0',
          'cursor-pointer items-center justify-center rounded-lg px-4 py-2',
          'text-sm font-medium text-white',
          isBanner ? 'md:w-auto' : 'w-full'
        )}
      >
        {t('message.platformMigrationCta', 'Move to the new platform')}
      </a>
    </div>
  );
}

export default PlatformMigrationNotice;
